import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import authService from "../../api/AuthService";
import Config from "../../utils/Config";

// Async thunks
export const login = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      if (response.data?.status_code === 200 && response.data?.message === "Action Successful") {
        const userData = response.data?.data;
        localStorage.setItem(Config.TOKEN_KEY, userData?.token);
        localStorage.setItem(Config.USER_KEY, JSON.stringify(userData));
        return userData;
      } else {
        return rejectWithValue(response.data?.reason || response.data?.message || "Login failed");
      }
    } catch (error) {
      return rejectWithValue(error.message || "An error occurred during login");
    }
  }
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      return true;
    } catch (error) {
      // Even if logout fails, clear local storage
      localStorage.removeItem(Config.TOKEN_KEY);
      localStorage.removeItem(Config.USER_KEY);
      return rejectWithValue(error.message || "Error logging out");
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  "auth/getCurrentUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getCurrentUser();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to get user data");
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (email, { rejectWithValue }) => {
    try {
      const response = await authService.forgotPassword(email);
      if (response.status) {
        return response.message || "Reset email sent successfully";
      } else {
        return rejectWithValue(
          response.message || "Failed to send reset email"
        );
      }
    } catch (error) {
      return rejectWithValue(
        error.message || "An error occurred while sending reset email"
      );
    }
  }
);

export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await authService.verifyOtp(email, otp);
      if (response.status) {
        return response.message || "OTP verified successfully";
      } else {
        return rejectWithValue(response.message || "Invalid OTP");
      }
    } catch (error) {
      return rejectWithValue(
        error.message || "An error occurred while verifying OTP"
      );
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (
    { email, otp, password, password_confirmation },
    { rejectWithValue }
  ) => {
    try {
      const response = await authService.resetPassword(
        email,
        otp,
        password,
        password_confirmation
      );
      if (response.status) {
        return response.message || "Password reset successfully";
      } else {
        return rejectWithValue(response.message || "Failed to reset password");
      }
    } catch (error) {
      return rejectWithValue(
        error.message || "An error occurred while resetting password"
      );
    }
  }
);

const loadInitialState = () => {
  const user = localStorage.getItem(Config.USER_KEY);
  const token = localStorage.getItem(Config.TOKEN_KEY);
  let parsedUser = null;
  if(user){
    try{
      parsedUser = JSON.parse(user);
    }catch(error){
      console.error("Error parsing user from localStorage:", error);
      localStorage.removeItem(Config.USER_KEY);
    }
  }
  return {
    user: parsedUser,
    token: token || null,
    isAuthenticated: !!token,
  }
}

// Initial state
const initialState = loadInitialState();

// Auth slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearForgotPasswordSuccess: (state) => {
      state.forgotPasswordSuccess = false;
    },
    clearOtpVerified: (state) => {
      state.otpVerified = false;
    },
    clearResetPasswordSuccess: (state) => {
      state.resetPasswordSuccess = false;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    setToken: (state, action) => {
      state.token = action.payload;
      state.isAuthenticated = true;
    },
    logoutSuccess: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      state.forgotPasswordSuccess = false;
      state.otpVerified = false;
      state.resetPasswordSuccess = false;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.token = action.payload?.token;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
        state.forgotPasswordSuccess = false;
        state.otpVerified = false;
        state.resetPasswordSuccess = false;
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // Still clear the state even if logout fails
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      });

    // Get Current User
    builder
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.user = null;
        state.isAuthenticated = false;
      });

    // Forgot Password
    builder
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.forgotPasswordSuccess = false;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false;
        state.forgotPasswordSuccess = true;
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.forgotPasswordSuccess = false;
      });

    // Verify OTP
    builder
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.otpVerified = false;
      })
      .addCase(verifyOtp.fulfilled, (state) => {
        state.loading = false;
        state.otpVerified = true;
        state.error = null;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.otpVerified = false;
      });

    // Reset Password
    builder
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.resetPasswordSuccess = false;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        state.resetPasswordSuccess = true;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.resetPasswordSuccess = false;
      });
  },
});

// Export actions
export const {
  clearError,
  clearForgotPasswordSuccess,
  clearOtpVerified,
  clearResetPasswordSuccess,
  setUser,
  setToken,
  logoutSuccess,
} = authSlice.actions;

// Export selectors
export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectToken = (state) => state.auth.token;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectForgotPasswordSuccess = (state) =>
  state.auth.forgotPasswordSuccess;
export const selectOtpVerified = (state) => state.auth.otpVerified;
export const selectResetPasswordSuccess = (state) =>
  state.auth.resetPasswordSuccess;

export default authSlice.reducer;
